// First-party password recovery (B1): POST /api/v1/auth/password/forgot and
// POST /api/v1/auth/password/reset. Both are public, so the request goes out without the
// session bearer (a stale session must not trigger the shared client's 401 sign-out).
// Validation reuses the signup rules so both forms agree on what a valid password is.
import axios from 'axios';
import config from '../config';
import {MESSAGES, isEmailAddress, newIdempotencyKey, passwordIsStrong} from './signup';

const FORGOT_PATH = '/api/v1/auth/password/forgot';
const RESET_PATH = '/api/v1/auth/password/reset';

/**
 * POST a public recovery request (no bearer token). The key belongs to one user action: a retry
 * of the same request passes the same key (see hooks/useIdempotencyKeys).
 */
const postPublic = (http, path, body, idempotencyKey = newIdempotencyKey()) =>
  http.post(`${config.apiBaseUrl}${path}`, body, {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'Idempotency-Key': idempotencyKey,
    },
  });

export const RESET_MESSAGES = Object.freeze({
  /** Same text whether or not the address has an account (no enumeration). */
  sent: 'If an account exists for that email, we sent a reset link.',
  unavailable: 'Password reset is unavailable right now. Please try again later.',
  linkInvalid: 'This reset link has expired or was already used.',
  linkIncomplete: 'This reset link is incomplete.',
  linkRequestNew: 'Request a new reset link',
  updated: 'Your password has been updated. Sign in with your new password.',
  confirmRequired: 'Confirm your new password.',
});

/** Rejection value of the reset calls: a user-facing `message`, a `code` and `{field: message}`. */
export class PasswordResetError extends Error {
  constructor(message, {code = 'unavailable', fieldErrors = {}} = {}) {
    super(message);
    this.name = 'PasswordResetError';
    this.code = code;
    this.fieldErrors = fieldErrors;
  }
}

/** The reset token from `?token=` only (the legacy `?key=` parameter is not honoured). */
export const tokenFromSearch = (search) => {
  try {
    return (new URLSearchParams(search || '').get('token') || '').trim();
  } catch {
    return '';
  }
};

/** Client-side check for the forgot form. Returns `{email: message}` or `{}`. */
export const validateForgot = (values) => {
  const email = typeof values?.email === 'string' ? values.email.trim() : '';
  if (!email) return {email: MESSAGES.email};
  if (!isEmailAddress(email)) return {email: MESSAGES.emailFormat};
  return {};
};

/**
 * Client-side check for the new-password form. Keys are form fields (`password`,
 * `confirm_password`). Same rule as signup: 8+ characters with a letter and a number.
 */
export const validateReset = (values) => {
  const errors = {};
  const password = typeof values?.password === 'string' ? values.password : '';
  const confirm = typeof values?.confirm_password === 'string' ? values.confirm_password : '';
  if (!password) errors.password = MESSAGES.password;
  else if (!passwordIsStrong(password)) errors.password = MESSAGES.passwordRule;
  if (!confirm) errors.confirm_password = RESET_MESSAGES.confirmRequired;
  else if (password && confirm !== password) errors.confirm_password = MESSAGES.confirmMismatch;
  return errors;
};

/** Carries the HTTP status of the failed request on the wrapped error (see useIdempotencyKeys). */
const withStatus = (wrapped, err) => {
  wrapped.status = err && err.response ? err.response.status : undefined;
  return wrapped;
};

const problemOf = (err) => {
  const data = err && err.response && err.response.data;
  return data && typeof data === 'object' ? data : {};
};

const isInvalidTokenProblem = (problem) =>
  typeof problem.type === 'string' && problem.type.endsWith('invalid-reset-token');

/** Field errors from a 422 `errors[]`, keyed by form field (`email`, `password`, `token`). */
const fieldErrorsFrom = (problem, mapping) => {
  const out = {};
  for (const e of Array.isArray(problem.errors) ? problem.errors : []) {
    const field = e && mapping[e.field];
    if (field && !out[field]) out[field] = e.message;
  }
  return out;
};

/** Failed forgot-password request -> PasswordResetError. Only 422 carries field detail. */
export const forgotErrorFromResponse = (err) => {
  const status = err && err.response && err.response.status;
  if (status === 422) {
    const fieldErrors = fieldErrorsFrom(problemOf(err), {email: 'email'});
    if (fieldErrors.email) {
      return new PasswordResetError(fieldErrors.email, {code: 'validation', fieldErrors});
    }
  }
  return new PasswordResetError(RESET_MESSAGES.unavailable, {code: 'unavailable'});
};

/** Failed reset request -> PasswordResetError with `invalid-token`, `validation` or `unavailable`. */
export const resetErrorFromResponse = (err) => {
  const status = err && err.response && err.response.status;
  const problem = problemOf(err);
  if (status === 400 && isInvalidTokenProblem(problem)) {
    return new PasswordResetError(RESET_MESSAGES.linkInvalid, {code: 'invalid-token'});
  }
  if (status === 422) {
    // Presence of the field matters here, not its message: the server may send only a code.
    const fields = (Array.isArray(problem.errors) ? problem.errors : []).map((e) => e && e.field);
    if (fields.includes('token')) {
      return new PasswordResetError(RESET_MESSAGES.linkInvalid, {code: 'invalid-token'});
    }
    if (fields.includes('new_password')) {
      // Show the signup rule text, the same wording the form uses for a weak password.
      return new PasswordResetError(MESSAGES.passwordRule, {
        code: 'validation',
        fieldErrors: {password: MESSAGES.passwordRule},
      });
    }
  }
  return new PasswordResetError(RESET_MESSAGES.unavailable, {code: 'unavailable'});
};

/**
 * Asks for a reset link. Resolves on 202 whatever the account state is; the caller always shows
 * RESET_MESSAGES.sent. Rejects with a PasswordResetError (validation or unavailable).
 */
export const requestPasswordReset = async (email, {http = axios, idempotencyKey} = {}) => {
  try {
    await postPublic(http, FORGOT_PATH, {email: String(email).trim()}, idempotencyKey);
  } catch (err) {
    throw withStatus(forgotErrorFromResponse(err), err);
  }
};

/** Sets the new password with the emailed token. Resolves on 204; rejects with a PasswordResetError. */
export const confirmPasswordReset = async (token, newPassword, {http = axios, idempotencyKey} = {}) => {
  try {
    await postPublic(http, RESET_PATH, {token, new_password: newPassword}, idempotencyKey);
  } catch (err) {
    throw withStatus(resetErrorFromResponse(err), err);
  }
};
